/**
 * 서비스지역 API mock 데이터.
 *
 * 백엔드 schema 기준 sample 응답. dev 환경 default(IS_MOCK=true)에서 사용.
 * 실제 API 연동 시 `VITE_USE_MOCK=false` 또는 production 빌드로 자동 비활성.
 *
 * 데이터 출처:
 *  - 광역권역 리스트: schema sample 4개 (서울/경기/인천/경상)
 *  - 광역권역 상세:
 *    - 서울: schema sample 그대로 (강남구·관악구 — full coverage 케이스)
 *    - 경기: Figma 디자인 17690:39935 기준 17개 시 전부 (북부 7 + 남부 10)
 *      디자인 케이스 3종 모두 커버: 일반구 보유 / 일반구 없음+partial / isFullCoverage 정적
 *    - 인천·경상: 임시 minimal (시각 자산 + 디자인 자료 수령 후 보강)
 *
 * 백엔드 응답 구조와 1:1 — 실제 API로 전환 시 본 파일 폐기 가능.
 */

import type { Dong, IlbanGu, RegionDetail, RegionSummary, SiGunGu } from '@/types/serviceArea'

// ─────────────────────────────────────────────────────────
// 헬퍼
// ─────────────────────────────────────────────────────────

let dongIdSeq = 1000

/** 동 N개 placeholder 생성 — id는 mock 전역 unique, zoneId는 시군구 단위 묶음 */
const makeDongs = (count: number, zoneId: number, prefix = ''): Dong[] =>
  Array.from({ length: count }, (_, i) => ({
    id: dongIdSeq++,
    name: prefix ? `${prefix}${i + 1}동` : `${i + 1}번동`,
    zoneId,
  }))

/** 일반구 보유 시군구 — 일반구별 dongList */
const makeSiGunGuWithIlbanGu = (
  name: string,
  subRegion: string | null,
  ilbanGus: { name: string; isFullCoverage: boolean; dongCount: number; zoneId: number }[]
): SiGunGu => ({
  name,
  isFullCoverage: false,
  subRegion,
  ilbanGuList: ilbanGus.map<IlbanGu>((ig) => ({
    name: ig.name,
    isFullCoverage: ig.isFullCoverage,
    dongList: makeDongs(ig.dongCount, ig.zoneId, ig.name.replace(/구$/, '')),
  })),
  dongList: [],
})

/** 일반구 없는 partial 시군구 — dongList 직접 */
const makePartialSiGunGu = (name: string, subRegion: string | null, dongCount: number, zoneId: number): SiGunGu => ({
  name,
  isFullCoverage: false,
  subRegion,
  ilbanGuList: [],
  dongList: makeDongs(dongCount, zoneId, name.replace(/시$/, '')),
})

/**
 * isFullCoverage=true 시군구 — 펼침 가능 카드(DongAccordion + 칩) 노출 정책으로
 * dongList도 채워야 함. dongCount 안 받으면 4개 placeholder.
 */
const makeFullCoverageSiGunGu = (
  name: string,
  subRegion: string | null,
  dongCount: number = 4,
  zoneId: number = 0
): SiGunGu => ({
  name,
  isFullCoverage: true,
  subRegion,
  ilbanGuList: [],
  dongList: makeDongs(dongCount, zoneId, name.replace(/시$|구$/, '')),
})

// ─────────────────────────────────────────────────────────
// 광역권역 리스트
// ─────────────────────────────────────────────────────────

export const MOCK_REGION_LIST: RegionSummary[] = [
  { region: '서울', subRegionList: [], isFullCoverage: true },
  { region: '경기', subRegionList: ['경기 북부', '경기 남부'], isFullCoverage: false },
  { region: '인천', subRegionList: [], isFullCoverage: false },
  { region: '경상', subRegionList: ['부산', '경남'], isFullCoverage: false },
]

// ─────────────────────────────────────────────────────────
// 서울 — 광역권역 isFullCoverage=true (DistrictList 자체 숨김)
// ─────────────────────────────────────────────────────────

const MOCK_SEOUL_DETAIL: RegionDetail = {
  region: '서울',
  subRegionList: [],
  isFullCoverage: true,
  siDoList: [
    {
      name: '서울특별시',
      isFullCoverage: true,
      siGunGuList: [
        {
          name: '강남구',
          isFullCoverage: true,
          subRegion: null,
          ilbanGuList: [],
          dongList: makeDongs(14, 8, '강남'),
        },
        {
          name: '관악구',
          isFullCoverage: true,
          subRegion: null,
          ilbanGuList: [],
          dongList: makeDongs(21, 10, '관악'),
        },
      ],
    },
  ],
}

// ─────────────────────────────────────────────────────────
// 경기 — Figma 17690:39935 기준 17개 시 전부
// 북부 7개: 파주·양주·의정부·고양·남양주·구리·김포
// 남부 10개: 하남·부천·시흥·광명·안산·안양·성남·수원·군포·용인
// ─────────────────────────────────────────────────────────

const GYEONGGI_SI_GUN_GU_LIST: SiGunGu[] = [
  // ── 북부 7개 ──
  makePartialSiGunGu('파주시', '경기 북부', 9, 100),
  makePartialSiGunGu('양주시', '경기 북부', 9, 101),
  makeFullCoverageSiGunGu('의정부시', '경기 북부', 14, 500),
  makeSiGunGuWithIlbanGu('고양시', '경기 북부', [
    { name: '덕양구', isFullCoverage: true, dongCount: 11, zoneId: 110 },
    { name: '일산동구', isFullCoverage: false, dongCount: 7, zoneId: 111 },
    { name: '일산서구', isFullCoverage: false, dongCount: 6, zoneId: 112 },
  ]),
  makePartialSiGunGu('남양주시', '경기 북부', 3, 102),
  makeFullCoverageSiGunGu('구리시', '경기 북부', 8, 501),
  makePartialSiGunGu('김포시', '경기 북부', 4, 103),

  // ── 남부 10개 ──
  makePartialSiGunGu('하남시', '경기 남부', 5, 200),
  makeFullCoverageSiGunGu('부천시', '경기 남부', 36, 600),
  makePartialSiGunGu('시흥시', '경기 남부', 18, 201),
  makePartialSiGunGu('광명시', '경기 남부', 5, 202),
  makeSiGunGuWithIlbanGu('안산시', '경기 남부', [
    { name: '단원구', isFullCoverage: false, dongCount: 9, zoneId: 210 },
    { name: '상록구', isFullCoverage: false, dongCount: 7, zoneId: 211 },
  ]),
  makeFullCoverageSiGunGu('안양시', '경기 남부', 31, 601),
  makeSiGunGuWithIlbanGu('성남시', '경기 남부', [
    { name: '분당구', isFullCoverage: true, dongCount: 10, zoneId: 220 },
    { name: '수정구', isFullCoverage: false, dongCount: 8, zoneId: 221 },
    { name: '중원구', isFullCoverage: false, dongCount: 9, zoneId: 222 },
  ]),
  makeSiGunGuWithIlbanGu('수원시', '경기 남부', [
    { name: '권선구', isFullCoverage: false, dongCount: 13, zoneId: 230 },
    { name: '영통구', isFullCoverage: true, dongCount: 6, zoneId: 231 },
    { name: '장안구', isFullCoverage: false, dongCount: 9, zoneId: 232 },
    { name: '팔달구', isFullCoverage: false, dongCount: 12, zoneId: 233 },
  ]),
  makePartialSiGunGu('군포시', '경기 남부', 4, 203),
  makeSiGunGuWithIlbanGu('용인시', '경기 남부', [
    { name: '기흥구', isFullCoverage: false, dongCount: 17, zoneId: 240 },
    { name: '수지구', isFullCoverage: true, dongCount: 6, zoneId: 241 },
  ]),
]

const MOCK_GYEONGGI_DETAIL: RegionDetail = {
  region: '경기',
  subRegionList: ['경기 북부', '경기 남부'],
  isFullCoverage: false,
  siDoList: [{ name: '경기도', isFullCoverage: false, siGunGuList: GYEONGGI_SI_GUN_GU_LIST }],
}

// ─────────────────────────────────────────────────────────
// 인천 — Figma 디자인 17740:44478 기준 7개 시군구 (강화군·옹진군·동구 제외)
// ─────────────────────────────────────────────────────────

const MOCK_INCHEON_DETAIL: RegionDetail = {
  region: '인천',
  subRegionList: [],
  isFullCoverage: false,
  siDoList: [
    {
      name: '인천광역시',
      isFullCoverage: false,
      siGunGuList: [
        makePartialSiGunGu('계양구', null, 12, 300),
        makePartialSiGunGu('남동구', null, 9, 301),
        makeFullCoverageSiGunGu('미추홀구', null, 7, 700),
        makeFullCoverageSiGunGu('부평구', null, 9, 701),
        makePartialSiGunGu('서구', null, 20, 302),
        makeFullCoverageSiGunGu('연수구', null, 6, 702),
        makePartialSiGunGu('중구', null, 46, 303),
      ],
    },
  ],
}

// ─────────────────────────────────────────────────────────
// 경상 — 광역권역 안에 시도 2개 (부산광역시 + 경상남도) → 시도 헤더 노출 케이스
// ─────────────────────────────────────────────────────────

const MOCK_GYEONGSANG_DETAIL: RegionDetail = {
  region: '경상',
  subRegionList: ['부산', '경남'],
  isFullCoverage: false,
  siDoList: [
    {
      name: '부산광역시',
      isFullCoverage: false,
      siGunGuList: [
        makeFullCoverageSiGunGu('해운대구', '부산', 18, 800),
        makePartialSiGunGu('수영구', '부산', 7, 400),
      ],
    },
    {
      name: '경상남도',
      isFullCoverage: false,
      siGunGuList: [
        makeSiGunGuWithIlbanGu('창원시', '경남', [
          { name: '의창구', isFullCoverage: false, dongCount: 6, zoneId: 410 },
          { name: '성산구', isFullCoverage: true, dongCount: 8, zoneId: 411 },
          { name: '진해구', isFullCoverage: true, dongCount: 12, zoneId: 412 },
        ]),
        makePartialSiGunGu('진주시', '경남', 5, 402),
      ],
    },
  ],
}

// ─────────────────────────────────────────────────────────
// region 한글 → mock detail 매핑
// ─────────────────────────────────────────────────────────

const MOCK_DETAIL_BY_REGION: Record<string, RegionDetail> = {
  서울: MOCK_SEOUL_DETAIL,
  경기: MOCK_GYEONGGI_DETAIL,
  인천: MOCK_INCHEON_DETAIL,
  경상: MOCK_GYEONGSANG_DETAIL,
}

/**
 * mock 광역권역 상세 조회 — region/subRegion 매칭.
 *
 * subRegion이 주어지면 해당 subRegion에 속한 시군구만 필터링해서 반환.
 * 백엔드 동작과 동일하게: subRegionList는 그대로 두고 siDoList[].siGunGuList만 좁힘.
 */
export const findMockRegionDetail = (region: string, subRegion?: string | null): RegionDetail => {
  const detail = MOCK_DETAIL_BY_REGION[region]
  if (!detail) {
    throw new Error(`mock 데이터에 없는 region: ${region}`)
  }
  if (!subRegion) return detail

  return {
    ...detail,
    siDoList: detail.siDoList.map((siDo) => ({
      ...siDo,
      siGunGuList: siDo.siGunGuList.filter((siGunGu) => siGunGu.subRegion === subRegion),
    })),
  }
}
